// PrimeReact InputSwitch: onChange returns InputSwitchChangeEvent with `e.value`
// typed as boolean.
// Verified from existing usage:
//   - apps/web/src/pages/agent/DevicesPage.tsx:259 — onChange={(e) => setForm({ ...form, enabled: e.value ?? true })}

import { useId, type JSX } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { InputSwitch } from 'primereact/inputswitch'

interface Props {
  name: string
  label: string
  helpText?: string
  disabled?: boolean
}

export function FormSwitch({ name, label, helpText, disabled }: Props): JSX.Element {
  const { control, formState: { errors } } = useFormContext()
  const error = errors[name]
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined
  const id = useId()
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy = [helpText && helpId, errorMessage && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1 mb-3">
      <label htmlFor={id} className="text-sm font-medium">{label}</label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <InputSwitch
            inputId={id}
            checked={Boolean(field.value)}
            onChange={(e) => field.onChange(e.value)}
            onBlur={field.onBlur}
            disabled={disabled}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        )}
      />
      {helpText && <small id={helpId} className="text-gray-500">{helpText}</small>}
      {errorMessage && (
        <small id={errorId} role="alert" className="text-red-600">{errorMessage}</small>
      )}
    </div>
  )
}
